import { PersonaType, TenantProvider, TenantSubscriptionMetadata } from './TenantPersona';
import { DisplayData, TenantJobLine } from '~/lib/model';
import Big from 'big.js';
import { ZodVersionedMetadata, ZodVersionedSchema } from '~/lib/zod';
import { TenantId } from '~/tenants/common/registry';
import { TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import { v4 } from 'uuid';
import { BaseSchema } from 'valibot';
import { FileData, OrderCustomTag } from '~/lib/schema/order';
import { DeliverableType } from '~common/model/Deliverable';
import { DateTime, Duration } from 'luxon';
import { datetime } from '~/lib/datettime';
import { context_has_performable } from '~lib/context';
import { deliveryIsRush } from '~/lib/schema/delivery-menu';

export interface TenantPerformableConfig<
  Schema extends ZodVersionedSchema = ZodVersionedSchema,
  Id extends string = string,
> {
  id: Id;
  tenant: TenantId;
  name: string;
  short: string;
  package?: string;
  bullets?: string[];
  long?: string;
  title?: string;
  color?: string | ((metadata: Record<string, unknown>) => string);
  group?: string;
  schema: Schema;
  upsell?: string[];
  submit?: BaseSchema<any, any, any>;
  thumbnail?: string | ((context: Record<string, unknown>) => string);
  images: string[];
  /**
   * Marks this performable as unlocking the Bleenk premium video
   * suite when it is on an order (see CatalogProductDataSchema.
   * bleenk_video_bundle — same semantics). Lives on the CODE config
   * too so tenants that have not migrated to DB-driven catalog rows
   * (dynamic_config) can still carry the entitlement; the tenant-setup
   * exporter copies it onto the seeded catalog product row.
   */
  bleenk_video_bundle?: boolean;
}

export interface TenantJobInput {
  performables: string[];
  required: number;
  title: string;
  description: string;
}

export abstract class TenantJob<
  O extends TenantOrderContext = TenantOrderContext,
  P extends TenantPerformableConfig = TenantPerformableConfig,
  Provider extends TenantProvider = TenantProvider,
> {
  readonly metadata: ZodVersionedMetadata<P['schema']>;
  readonly id: string;

  constructor(
    public readonly order: O,
    id?: string,
    metadata?: ZodVersionedMetadata<P['schema']>,
    public provider?: Provider,
  ) {
    this.id = id ?? v4();
    this.metadata = metadata ?? this.defaultValue();
  }

  abstract get performable(): P;

  /**
   * NEIGHBORHOOD PHOTOS (lib/neighborhood-photos.ts). `neighborhoodShoot`: this
   * job shoots neighborhood amenities (Twist's Custom Neighborhood
   * Amenities). `houseShoot`: it is a photo shoot of the house. The order
   * decides from both which of its photos are amenity photos
   * (JobEntity.neighborhoodPhotos). Both false by default.
   */
  get neighborhoodShoot(): boolean {
    return false;
  }

  get houseShoot(): boolean {
    return false;
  }

  protected buyer<F extends keyof O['buyer']['metadata']>(field: F): O['buyer']['metadata'][F] {
    return (
      (this.order.buyer.metadata as O['buyer']['metadata'])[field] ??
      (this.order.parent as O['buyer']['metadata'])?.[field]
    );
  }

  protected orderHasPerformable<
    P extends O['jobs'][0]['performable_id'],
    M extends Extract<O['jobs'][0], { performable_id: P }>['metadata'],
  >(id: P, metadata: Partial<M> = {}) {
    return context_has_performable(this.order, id, metadata);
  }

  defaultValue(): ZodVersionedMetadata<P['schema']> {
    throw new Error(`defaultValue not implemented for ${this.constructor.name}`);
  }

  get assignee(): string | null {
    return null;
  }

  get editor(): string | null {
    return null;
  }

  get twilight() {
    return false;
  }

  get name() {
    return this.performable.name;
  }

  get short() {
    return this.name;
  }

  get confirm() {
    return false;
  }

  get assignable() {
    return Boolean(this.assignee || this.media === PersonaType.OFFICE);
  }

  get review(): boolean | string {
    return false;
  }

  get startWithin(): Duration | null {
    return null;
  }

  get completeWithin(): Duration | null {
    return null;
  }

  /**
   * When this job is due. For the photo job shot at the visit an order's
   * delivery promise counts from, that is the promised instant, so the
   * office's "overdue" alert (clean_order_alert) fires the moment a promise
   * is missed. Null for every other job, and on an order with no promise.
   */
  get completeBy(): DateTime | null {
    const release = this.order.delivery_release;

    if (!release?.promised_at || !release.shoot_appointment_id || this.deliverable !== DeliverableType.PHOTO) {
      return null;
    }

    const shoot = this.order.appointments.find((a) => a.id === release.shoot_appointment_id && !a.canceled);

    if (!shoot || !shoot.job_ids.includes(this.id)) {
      return null;
    }

    return datetime(release.promised_at as TDateISO, this.order.timezone);
  }

  get minimumPhotos(): number {
    return 0;
  }

  get maximumPhotos(): number {
    return this.minimumPhotos;
  }

  get estimate(): boolean {
    return this.revenueLines().some((r) => r.estimate) || this.hourly;
  }

  get inputs(): string[] {
    return [];
  }

  get eligible() {
    return true;
  }

  get hidden() {
    return false;
  }

  get input(): TenantJobInput | null {
    return null;
  }

  /**
   * A paid delivery promise makes the photo job a rush for whoever shoots and
   * edits it: the option's own name, on the board and the provider's
   * schedule, as Twist's "Rush"/"Priority" are. Only on an order that carries
   * a promise (order.data.delivery_release), so no other order gains a tag.
   */
  get tags(): OrderCustomTag[] {
    const release = this.order.delivery_release;

    if (!release || !deliveryIsRush(release) || this.deliverable !== DeliverableType.PHOTO) {
      return [];
    }

    return [{ name: release.option_name, color: release.days_after === 0 ? 'red' : 'yellow', provider: true }];
  }

  get dependencies(): string[] {
    return [];
  }

  get hourly() {
    return false;
  }

  get submitter(): PersonaType | null {
    return null;
  }

  get submission() {
    return false;
  }

  /**
   * "This job's photographer uploads BRACKETS that an automated engine
   * merges" — an explicit, per-job-model opt-in. The company switch alone
   * is not enough: with a tenant-wide default every PHOTO job took the EDIT
   * step, so MPI's human-editor photos, MPI's walkthrough video and aerial
   * jobs (which say `editing` for their own reasons) were dispatched to an
   * AI engine, and single-image products (Just Sold, twilight, headshots)
   * were sent to an engine that drops one-frame sets and never delivered
   * (review of #279). The listing-photo job models opt in; nothing else.
   */
  get aiEditing(): boolean {
    return false;
  }

  /**
   * "Edit this job with the automated engine." The company's editing switch
   * (the staff kill switch, which also gates the charge) AND the job model's
   * `aiEditing` opt-in. BRE overrides this with the photographer's per-job
   * ai_edit choice.
   */
  get autoenhance(): boolean {
    return this.order.editing?.enabled === true && this.aiEditing;
  }

  get libraries(): string[] {
    return [];
  }

  /**
   * Whether this job takes the EDIT step (uploads go to the editor, not the
   * customer). Company switch on + a photo job model that opted into AI
   * editing = yes. Legacy tenants that never turned the switch on are
   * unchanged; BRE/MPI override this with their own (human-editor) rules.
   */
  get editing(): boolean {
    return this.order.editing?.enabled === true && this.deliverable === DeliverableType.PHOTO && this.aiEditing;
  }

  get deliverable(): DeliverableType | null {
    return null;
  }

  get media(): PersonaType | null {
    return null;
  }

  get configurable() {
    return false;
  }

  get multiple() {
    return false;
  }

  revenueLines(): TenantJobLine[] {
    return [];
  }

  info(): DisplayData[] {
    return [];
  }

  revenue(discountable?: boolean): Big {
    return this.revenueLines()
      .filter((l) => !discountable || l.discountable)
      .reduce((sum, line) => sum.plus(line.amount), Big(0));
  }

  expenseLines(): TenantJobLine[] {
    return [];
  }

  expense(): Big {
    return this.expenseLines().reduce((sum, line) => sum.plus(line.amount), Big(0));
  }

  onsite(): number {
    return 0;
  }

  offsite(): number {
    return 0;
  }

  get files(): Pick<FileData, 'name' | 'bytes' | 'url' | 'mime'>[] {
    return [];
  }

  subscription(): TenantSubscriptionMetadata | null {
    return null;
  }

  protected get orderCreated() {
    return datetime(this.order.created, this.order.timezone);
  }

  protected get appointment() {
    const appointment = this.order.appointments.find((a) => a.job_ids.includes(this.id) && !a.canceled);

    if (!appointment) {
      return null;
    }

    return {
      ...appointment,
      jobs: this.order.jobs.filter((j) => appointment.job_ids.includes(j.id)),
    };
  }
}
